import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { SG_FETCH_CAMPAIGN } from 'constants/actions';

export default function Details() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const campaign = useSelector((s) => s.action_campaign?.[id]);

  useEffect(() => {
    if (id) dispatch({ type: SG_FETCH_CAMPAIGN, payload: { id } });
  }, [dispatch, id]);

  if (!campaign) return <div>Loading...</div>;

  const fmt = (v) => (v ? new Date(v).toLocaleString() : "-");

  function KeyValue({ label, value }) {
    return (
      <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid #f3f3f3' }}>
        <div style={{ minWidth: 160, color: '#444', fontWeight: 700 }}>{label}</div>
        <div style={{ color: '#222', flex: 1 }}>{value ?? '-'}</div>
      </div>
    );
  }

  function Section({ title, children }) {
    return (
      <section style={{ padding: 14, border: '1px solid #eee', borderRadius: 8, background: '#fff', marginBottom: 12 }}>
        <h4 style={{ margin: '0 0 8px 0' }}>{title}</h4>
        {children}
      </section>
    );
  }

  function PrettyJSON({ obj }) {
    const text = JSON.stringify(obj || {}, null, 2);
    const copy = () => { try { if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(text); } catch (e) { } };
    return (
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', right: 8, top: 8 }}>
          <button onClick={copy} style={{ border: 'none', background: '#0b5fff', color: '#fff', padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Copy</button>
        </div>
        <pre aria-label="json" style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12, borderRadius: 6, maxHeight: 360, overflow: 'auto', marginTop: 24 }}>{text}</pre>
      </div>
    );
  }

  const reportsGenerated = campaign.campaign_stats?.reports_generated ?? campaign.reports_count ?? 0;
  const reportsSent = campaign.campaign_stats?.reports_sent ?? campaign.debrief_report?.reports_sent ?? 0;

  return (
    <div style={{ maxWidth: 980, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0 }}>{campaign.name} <small style={{ color: '#666', marginLeft: 8 }}>{campaign.id}</small></h2>
          <div style={{ color: '#555', marginTop: 8 }}>{campaign.description || <em>No description</em>}</div>
          <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>{campaign.created_by || campaign.created_by_name || ''}</div>
        </div>

        <div style={{ minWidth: 220, textAlign: 'right' }}>
          <div><strong>Status:</strong> <span style={{ marginLeft: 8, padding: '6px 8px', borderRadius: 999, background: campaign.status === 'draft' ? '#f4f4f4' : '#eaf5ff', color: campaign.status === 'draft' ? '#666' : '#0b5fff', fontWeight: 700 }}>{campaign.status}</span></div>
          <div style={{ marginTop: 8 }}><strong>Phase:</strong> <span style={{ marginLeft: 8 }}>{campaign.current_phase || '-'}</span></div>
          <div style={{ marginTop: 6, color: '#666' }}>Start: {fmt(campaign.start_date)}</div>
          <div style={{ color: '#666' }}>End: {fmt(campaign.end_date)}</div>
        </div>
      </div>

      {/* Developer Notes */}
      <Section title="🔍 Developer Notes — Why Not Progressing?">
        <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 8 }}>Campaign Phase Progression Checks</div>
          <div style={{ color: '#78350f', fontSize: 13, lineHeight: '1.6' }}>
            <div style={{ marginBottom: 6 }}><strong>Current:</strong> {campaign.status} / {campaign.current_phase}</div>
            {campaign.status === 'draft' && <div style={{ color: '#dc2626' }}>⚠️ Campaign still in draft — activate to start</div>}
            {campaign.current_phase === 'setup' && <div style={{ color: '#dc2626' }}>⚠️ Still in setup phase — configure managers and dates</div>}
            {campaign.current_phase === 'report_generation' && reportsGenerated === 0 && <div style={{ color: '#dc2626' }}>⚠️ Reports generated: {reportsGenerated} — check report generation job</div>}
            {campaign.current_phase === 'report_generation' && reportsGenerated > 0 && reportsSent === 0 && <div style={{ color: '#d97706' }}>⚠️ Reports sent: {reportsSent}/{reportsGenerated} — reports not sent yet</div>}
            {campaign.current_phase === 'action_planning' && (!campaign.action_planning_launch_date || new Date(campaign.action_planning_launch_date) > new Date()) && <div style={{ color: '#d97706' }}>⚠️ Action planning launch date not reached: {fmt(campaign.action_planning_launch_date)}</div>}
            {campaign.auto_progress_phases === false && <div style={{ color: '#2563eb' }}>ℹ️ Auto-progress disabled — manual phase advancement required</div>}
            {campaign.require_admin_approval && <div style={{ color: '#2563eb' }}>ℹ️ Requires admin approval to advance</div>}
            {!campaign.selected_managers?.length && <div style={{ color: '#dc2626' }}>⚠️ No managers selected — campaign cannot generate reports</div>}
            {!campaign.debrief_report?.id && <div style={{ color: '#dc2626' }}>⚠️ No debrief report attached — reports cannot be generated</div>}
          </div>
        </div>
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', color: '#0b5fff' }}>📖 View validation guide (from docs)</summary>
          <div style={{ marginTop: 8, padding: 12, background: '#f9fafb', borderRadius: 6, fontSize: 12, fontFamily: 'monospace' }}>
            <div><strong>Expected Phase Flow:</strong></div>
            <div>Draft → Active/report_generation → Active/action_planning → Active/check_ins → Completed</div>
            <div style={{ marginTop: 8 }}><strong>Red Flags:</strong></div>
            <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
              <li>current_phase=report_generation but reports_generated=0 for &gt;1 hour</li>
              <li>auto_progress_phases=true but stuck in same phase for days</li>
              <li>status=active but current_phase=completed (inconsistent)</li>
            </ul>
          </div>
        </details>
      </Section>

      <Section title="Key summary">
        <KeyValue label="Organization" value={`${campaign.organization?.name || '-'} (${campaign.organization?.id ?? '-'})`} />
        <KeyValue label="Created" value={fmt(campaign.created_at)} />
        <KeyValue label="Updated" value={fmt(campaign.updated_at)} />
        <KeyValue label="Report generation date" value={fmt(campaign.report_generation_date)} />
        <KeyValue label="Report send date" value={fmt(campaign.report_send_date)} />
        <KeyValue label="Action planning launch" value={fmt(campaign.action_planning_launch_date)} />
        <KeyValue label="Check-in frequency (days)" value={campaign.check_in_frequency_days ?? '-'} />
        <KeyValue label="Total check-ins planned" value={campaign.total_check_ins_planned ?? '-'} />
        <KeyValue label="Auto progress phases" value={campaign.auto_progress_phases ? 'Yes' : 'No'} />
        <KeyValue label="Require admin approval" value={campaign.require_admin_approval ? 'Yes' : 'No'} />
      </Section>

      <Section title="Campaign statistics">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          {campaign.campaign_stats && Object.entries(campaign.campaign_stats).map(([k, v]) => (
            <div key={k} style={{ padding: 10, border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }}>
              <div style={{ fontSize: 12, color: '#666', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{String(v)}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Managers (${(campaign.selected_managers || []).length})`}>
        {campaign.selected_managers && campaign.selected_managers.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {campaign.selected_managers.map((m) => (
              <div key={m.id} style={{ padding: 10, border: '1px solid #eee', borderRadius: 8, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{m.first_name} {m.last_name} <small style={{ color: '#999', marginLeft: 8 }}>{m.id}</small></div>
                  <div style={{ color: '#666', fontSize: 12 }}>{m.email || ''} • {m.organization_name || m.organization}</div>
                </div>
                <div style={{ textAlign: 'right', color: '#666', fontSize: 12 }}>{m.status ? `status: ${m.status}` : ''}<div style={{ marginTop: 6 }}>created: {fmt(m.created_at)}</div></div>
              </div>
            ))}
          </div>
        ) : (
          <div>No managers selected</div>
        )}

        <div style={{ marginTop: 8 }}>
          <strong>Target groups</strong>
          {(campaign.target_groups && campaign.target_groups.length) ? (
            <ul style={{ marginTop: 8 }}>
              {campaign.target_groups.map((g, i) => (<li key={i}>{g}</li>))}
            </ul>
          ) : (
            <div style={{ color: '#666', marginTop: 6 }}>None</div>
          )}
        </div>
      </Section>

      <Section title="Phase history">
        {campaign.phase_history && campaign.phase_history.length ? (
          <ol>
            {campaign.phase_history.map((p, idx) => (<li key={idx}>{p}</li>))}
          </ol>
        ) : (
          <div>No phase history</div>
        )}
      </Section>

      <Section title="Debrief report">
        {campaign.debrief_report ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <KeyValue label="Report ID" value={campaign.debrief_report.id} />
              <KeyValue label="Type" value={`${campaign.debrief_report.report_type_display || campaign.debrief_report.report_type || '-' }`} />
              <KeyValue label="Organization" value={`${campaign.debrief_report.organization_name || campaign.debrief_report.organization || '-'}`} />
              <KeyValue label="Report name" value={campaign.debrief_report.report_name || '-'} />
              <KeyValue label="Created" value={fmt(campaign.debrief_report.created_at)} />
              <KeyValue label="Updated" value={fmt(campaign.debrief_report.updated_at)} />
            </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ fontWeight: 700 }}>Survey – includeData</div>
                  <div style={{ fontSize: 12, color: reportsGenerated ? '#0a6' : '#b85' }}>{reportsGenerated ? `Reports generated: ${reportsGenerated}` : 'No reports generated'}</div>
                </div>
                <PrettyJSON obj={campaign.debrief_report.includeData || {}} />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 6 }}>
                  <div style={{ fontWeight: 700 }}>Template params / branding</div>
                  <div style={{ fontSize: 12, color: reportsSent ? '#0a6' : '#b85' }}>{reportsSent ? `Reports sent: ${reportsSent}` : 'No reports sent'}</div>
                </div>
                <PrettyJSON obj={campaign.debrief_report.params?.additional_params || {}} />
              </div>
          </div>
        ) : (
          <div>No debrief report attached</div>
        )}
      </Section>

      <Section title="Raw payload">
        <PrettyJSON obj={campaign} />
      </Section>
    </div>
  );
}
